import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import api from '../lib/api';
import { useCustomerAuth } from './CustomerAuthContext';

const CartContext = createContext(null);
const STORAGE_KEY = 'betti_cart_v1';

export const CartProvider = ({ children }) => {
  const { customer } = useCustomerAuth();
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const syncedRef = useRef(false);

  // Initial load from localStorage
  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) setItems(JSON.parse(saved));
    } catch (e) {
      console.warn('[cart] failed to read cart from localStorage:', e);
    }
    setLoaded(true);
  }, []);

  // Listen for customer logout to clear cart (prevents cross-account leakage)
  useEffect(() => {
    const handler = () => {
      setItems([]);
      try { localStorage.removeItem(STORAGE_KEY); } catch (e) { console.warn('[cart] localStorage removeItem failed:', e); }
      syncedRef.current = false;
    };
    window.addEventListener('customer:logout', handler);
    return () => window.removeEventListener('customer:logout', handler);
  }, []);

  // When customer logs in: merge local with server cart
  useEffect(() => {
    if (!loaded) return;
    if (customer && !syncedRef.current) {
      syncedRef.current = true;
      (async () => {
        try {
          const res = await api.get('/customers/me/cart');
          const serverItems = res.data.items || [];
          // Merge: keep all unique by product_id, summing quantities
          const merged = mergeItems(items, serverItems);
          setItems(merged);
          // Push merged to server
          await api.put('/customers/me/cart', { items: merged });
        } catch (e) {
          console.warn('[cart] server sync failed on login:', e);
        }
      })();
    }
    if (!customer) {
      syncedRef.current = false;
    }
  }, [customer, loaded]); // eslint-disable-line react-hooks/exhaustive-deps

  // Persist on change
  useEffect(() => {
    if (!loaded) return;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    if (customer) {
      // Debounced server save
      const t = setTimeout(() => {
        api.put('/customers/me/cart', { items }).catch((e) => console.warn('[cart] server save failed:', e));
      }, 500);
      return () => clearTimeout(t);
    }
  }, [items, loaded, customer]);

  const addItem = (product, quantity = 1) => {
    setItems((prev) => {
      const existing = prev.find((i) => i.product_id === product.id);
      if (existing) {
        return prev.map((i) => i.product_id === product.id ? { ...i, quantity: i.quantity + quantity } : i);
      }
      return [
        ...prev,
        {
          product_id: product.id,
          product_name: product.name,
          product_slug: product.slug,
          brand: product.brand || '',
          image_url: product.image_url || '',
          price_per_day: product.price_per_day || 0,
          category_id: product.category_id || null,
          quantity,
        },
      ];
    });
  };

  // updateQty: minimum 1 — never removes via +/-
  const updateQty = (productId, quantity) => {
    const q = Math.max(1, parseInt(quantity) || 1);
    setItems((prev) => prev.map((i) => i.product_id === productId ? { ...i, quantity: q } : i));
  };

  // Only this method removes a product
  const removeItem = (productId) => setItems((prev) => prev.filter((i) => i.product_id !== productId));

  const clear = () => setItems([]);

  const itemCount = items.reduce((sum, i) => sum + i.quantity, 0);

  return (
    <CartContext.Provider value={{ items, addItem, updateQty, removeItem, clear, itemCount }}>
      {children}
    </CartContext.Provider>
  );
};

function mergeItems(a, b) {
  const map = new Map();
  for (const it of a) map.set(it.product_id, { ...it });
  for (const it of b) {
    if (map.has(it.product_id)) {
      const cur = map.get(it.product_id);
      cur.quantity = Math.max(cur.quantity, it.quantity);
      map.set(it.product_id, cur);
    } else {
      map.set(it.product_id, { ...it });
    }
  }
  return Array.from(map.values());
}

export const useCart = () => useContext(CartContext);
